import createHttp from '@/config/http';
import type {
	AcceptLicenseResponse,
	ChangePasswordResponse,
	Disable2FAResponse,
	Enable2FAResponse,
	InviteUserResponse,
	PasswordResetCompletionResponse,
	PasswordResetOtpRequestResponse,
	PasswordResetOtpVerificationResponse,
	ResetPasswordResponse,
	UpdatePasswordResponse,
} from '@/types/auth';

const http = createHttp(import.meta.env.VITE_AUTH_URL);

export const login = async (username: string, password: string) => {
	const response = await http.post('/auth/login', {
		username,
		password,
	});
	return response.data;
};

export const updatePassword = async (
	email: string,
	currentPassword: string,
): Promise<UpdatePasswordResponse> => {
	const response = await http.patch<UpdatePasswordResponse>(
		`/auth/update-password`,
		{
			email,
			currentPassword,
		},
	);
	return response.data;
};

export const inviteUser = async (
	email: string,
	fullName: string,
): Promise<InviteUserResponse> => {
	const response = await http.post<InviteUserResponse>(`/auth/invite-user`, {
		email,
		fullName,
	});
	return response.data;
};

export const resetPassword = async (
	email: string,
): Promise<ResetPasswordResponse> => {
	const response = await http.patch<ResetPasswordResponse>(
		`/auth/reset-password`,
		{
			email,
		},
	);
	return response.data;
};

export const changePassword = async (
	password: string,
	accessToken: string,
): Promise<ChangePasswordResponse> => {
	const response = await http.patch<ChangePasswordResponse>(
		`/auth/change-password`,
		{
			password,
		},
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	);
	return response.data;
};

export const requestPasswordResetOtp = async (
	email: string,
): Promise<PasswordResetOtpRequestResponse> => {
	const response = await http.patch<PasswordResetOtpRequestResponse>(
		`/auth/reset-password`,
		{
			email,
			action: 'request-otp',
		},
	);
	return response.data;
};

export const verifyPasswordResetOtp = async (
	email: string,
	otp: string,
): Promise<PasswordResetOtpVerificationResponse> => {
	const response = await http.patch<PasswordResetOtpVerificationResponse>(
		`/auth/reset-password`,
		{
			email,
			otp,
			action: 'verify-otp',
		},
	);
	return response.data;
};

export const completePasswordReset = async (
	email: string,
	password: string,
	resetToken: string,
): Promise<PasswordResetCompletionResponse> => {
	const response = await http.patch<PasswordResetCompletionResponse>(
		`/auth/reset-password`,
		{
			email,
			password,
			resetToken,
			action: 'update-password',
		},
	);
	return response.data;
};

export const sendOTP = async (email: string) => {
	const response = await http.post('/auth/2fa/request-otp', {
		email,
	});
	return response.data;
};

export const verifyOTP = async (otp: string, email?: string) => {
	const response = await http.post('/auth/2fa/verify-otp', {
		otp,
		...(email && { email }),
	});
	return response.data;
};

export const resendOTP = async (email: string) => {
	const response = await http.post('/auth/2fa/resend-otp', {
		email,
	});
	return response.data;
};

export const enable2FA = async (email: string, password: string): Promise<Enable2FAResponse> => {
	const response = await http.post<Enable2FAResponse>('/auth/2fa/enable', {
		email,
		password,
	});
	return response.data;
};

export const disable2FA = async (email: string, password: string): Promise<Disable2FAResponse> => {
	const response = await http.post<Disable2FAResponse>('/auth/2fa/disable', {
		email,
		password,
	});
	return response.data;
};

export const acceptLicenseAgreement = async (email: string): Promise<AcceptLicenseResponse> => {
	const response = await http.post<AcceptLicenseResponse>('/auth/accept-license', {
		email,
	});
	return response.data;
};
